import { useState, useEffect } from 'react'
import FormModal from '../FormModal'
import DdbSpecEditor from './DdbSpecEditor'
import { parseDdbSpec } from '../../utils/ddbExport'

const formStyles = {
  field: {
    marginBottom: '0.4rem',
  },
  label: {
    display: 'block',
    color: 'var(--text-primary)',
    marginBottom: '0.4rem',
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    minHeight: '120px',
    resize: 'vertical',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  option: {
    background: 'var(--accent-deep)',
    color: 'var(--text-primary)',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  checkboxInput: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
}

function ItemForm({ isOpen, onClose, item = null, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    full_description: '',
    item_type: 'misc',
    icon: '',
    is_visible: 0,
    ddb_spec: null,
    ddb_url: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [iconFile, setIconFile] = useState(null)
  const [iconPreview, setIconPreview] = useState(null)
  const [deleteIcon, setDeleteIcon] = useState(false)

  // Update form data when item prop changes
  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        full_description: item.full_description || '',
        item_type: item.item_type || 'misc',
        icon: item.icon || '',
        is_visible: item.is_visible || 0,
        ddb_spec: parseDdbSpec(item.ddb_spec),
        ddb_url: item.ddb_url || '',
      })
      // Set existing icon preview if available
      setIconPreview(item.icon_url || null)
      setIconFile(null)
      setDeleteIcon(false)
    } else {
      setFormData({
        name: '',
        description: '',
        full_description: '',
        item_type: 'misc',
        icon: '',
        is_visible: 0,
        ddb_spec: null,
        ddb_url: '',
      })
      setIconPreview(null)
      setIconFile(null)
      setDeleteIcon(false)
    }
  }, [item])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }))
  }

  const handleIconChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setIconFile(file)
      setDeleteIcon(false) // Clear delete flag when uploading new file
      // Create preview URL
      const reader = new FileReader()
      reader.onloadend = () => {
        setIconPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveIcon = () => {
    setIconFile(null)
    setIconPreview(null)
    setDeleteIcon(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Pass form data, icon file, and delete flag to parent
      await onSubmit(formData, iconFile, deleteIcon)
      onClose()
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={item ? 'Edit Item' : 'Create New Item'}
      isSubmitting={isSubmitting}
    >
      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="name">Name *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          style={formStyles.input}
          placeholder="Enter item name"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <label style={formStyles.label} htmlFor="item_type">Item Type</label>
          <select
            id="item_type"
            name="item_type"
            value={formData.item_type}
            onChange={handleChange}
            style={formStyles.select}
          >
            <option value="quest_item" style={formStyles.option}>Quest Item</option>
            <option value="artifact" style={formStyles.option}>Artifact</option>
            <option value="weapon" style={formStyles.option}>Weapon</option>
            <option value="armor" style={formStyles.option}>Armor</option>
            <option value="consumable" style={formStyles.option}>Consumable</option>
            <option value="wondrous" style={formStyles.option}>Wondrous Item</option>
            <option value="misc" style={formStyles.option}>Miscellaneous</option>
          </select>
        </div>

        <div>
          <label style={formStyles.label} htmlFor="icon">Icon (Emoji)</label>
          <input
            type="text"
            id="icon"
            name="icon"
            value={formData.icon}
            onChange={handleChange}
            style={formStyles.input}
            placeholder="📦"
          />
        </div>
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="icon_image">Icon Image (optional)</label>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
          {iconPreview && (
            <div style={{ position: 'relative' }}>
              <img
                src={iconPreview}
                alt="Icon preview"
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  border: '2px solid var(--accent-teal)'
                }}
              />
              <button
                type="button"
                onClick={handleRemoveIcon}
                style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  background: '#c0392b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Remove icon"
              >
                ×
              </button>
            </div>
          )}
          <div style={{ flex: 1 }}>
            <input
              type="file"
              id="icon_image"
              name="icon_image"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
              onChange={handleIconChange}
              style={{
                ...formStyles.input,
                cursor: 'pointer',
                padding: '0.5rem'
              }}
            />
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Upload an icon image (JPEG, PNG, WebP, or GIF, max 5MB). Falls back to emoji if not provided.
            </div>
          </div>
        </div>
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="description">Description * <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.85rem' }}>(shown on card)</span></label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          style={{ ...formStyles.textarea, minHeight: '110px' }}
          placeholder="Brief description shown on the item card"
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="full_description">Full Description <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.85rem' }}>(shown in expanded view)</span></label>
        <textarea
          id="full_description"
          name="full_description"
          value={formData.full_description}
          onChange={handleChange}
          style={{ ...formStyles.textarea, minHeight: 'clamp(220px, 40vh, 600px)' }}
          placeholder="Detailed item lore, properties, and history shown when clicking on the item card"
        />
      </div>

      <DdbSpecEditor
        value={formData.ddb_spec}
        onChange={(ddb_spec) => setFormData(prev => ({ ...prev, ddb_spec }))}
        styles={formStyles}
      />

      {formData.ddb_spec && (
        <div style={formStyles.field}>
          <label style={formStyles.label} htmlFor="ddb_url">D&amp;D Beyond link <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.85rem' }}>(paste the item's DDB page after importing it)</span></label>
          <input
            type="url"
            id="ddb_url"
            name="ddb_url"
            value={formData.ddb_url}
            onChange={handleChange}
            style={formStyles.input}
            placeholder="https://www.dndbeyond.com/magic-items/..."
          />
        </div>
      )}

      <div style={formStyles.field}>
        <label style={formStyles.checkbox}>
          <input
            type="checkbox"
            name="is_visible"
            checked={formData.is_visible === 1}
            onChange={handleChange}
            style={formStyles.checkboxInput}
          />
          <span style={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>
            Visible to players
          </span>
        </label>
      </div>
    </FormModal>
  )
}

export default ItemForm
